import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  BadgeAlert, CheckCircle2, ChevronLeft, ChevronRight, ClipboardList,
  Cloud, Download, KeyRound, Loader2, RefreshCw, Search, Settings2,
} from "lucide-react";

const errMsg = (e, fallback) => e?.response?.data?.detail || fallback;
const FLOOR_DATE = "2026-01-01";
const todayISO = () => new Date().toISOString().slice(0, 10);
const fmtInt = (v) => (Number(v) || 0).toLocaleString("en-AU");
const fmtDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("en-AU", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
};

/* ── Config dialog ─────────────────────────────────────────────── */
function ConfigDialog({ open, onOpenChange, config, onSaved }) {
  const [apiKey, setApiKey] = useState("");
  const [baseUrl, setBaseUrl] = useState("");
  const [path, setPath] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setApiKey("");
      setBaseUrl(config?.base_url || "");
      setPath(config?.path || "");
    }
  }, [open, config]);

  const save = async () => {
    setSaving(true);
    try {
      const body = { base_url: baseUrl, path };
      if (apiKey.trim()) body.api_key = apiKey.trim();
      const { data } = await api.put("/worklogs/config", body);
      toast.success("Config saved");
      onSaved && onSaved(data);
      onOpenChange(false);
    } catch (e) { toast.error(errMsg(e, "Save failed")); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="worklogs-config-dialog">
        <DialogHeader>
          <DialogTitle>Iceberg CMS · Work Logs API</DialogTitle>
          <DialogDescription>
            Credentials for pulling work-element data from the Iceberg CMS job
            manager. Saved encrypted at rest — the key is never shown again once saved.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">API key</Label>
            <Input type="password" value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder={config?.has_api_key ? "•••••••• (leave blank to keep existing)" : "kZ9r..."}
              data-testid="worklogs-config-apikey" />
            {config?.has_api_key && (
              <div className="text-[11px] text-emerald-700 mt-1">
                Key is saved. Enter a new one only to replace it.
              </div>
            )}
          </div>
          <div>
            <Label className="text-xs">Base URL</Label>
            <Input value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)}
              placeholder="https://jobmanager.icebergsoftware.au"
              data-testid="worklogs-config-baseurl" />
          </div>
          <div>
            <Label className="text-xs">Endpoint path</Label>
            <Input value={path} onChange={(e) => setPath(e.target.value)}
              placeholder="/api/cms/workelements/external/list"
              data-testid="worklogs-config-path" />
            <div className="text-[11px] text-zinc-500 mt-1">
              Default: <code className="text-[10px]">/api/cms/workelements/external/list</code>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving}
            data-testid="worklogs-config-save-btn">
            {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ── Sync panel ────────────────────────────────────────────────── */
function SyncPanel({ config, onConfigure, onSynced }) {
  const [start, setStart] = useState(config?.last_synced_range?.end
    ? config.last_synced_range.end : FLOOR_DATE);
  const [end, setEnd] = useState(todayISO());
  const [forceRefetch, setForceRefetch] = useState(false);
  const [job, setJob] = useState(null);
  const [starting, setStarting] = useState(false);
  const [history, setHistory] = useState([]);

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await api.get("/worklogs/sync");
      setHistory(data.jobs || []);
      const running = (data.jobs || []).find((j) => j.status === "running");
      if (running) setJob(running);
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  // Poll running job
  useEffect(() => {
    if (!job || job.status !== "running") return undefined;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/worklogs/sync/${job.job_id}`);
        setJob(data);
        if (data.status !== "running") {
          if (data.status === "done") {
            toast.success(`Sync done · ${fmtInt(data.rows_fetched)} rows · ${fmtInt(data.inserted)} new, ${fmtInt(data.updated)} updated`);
            onSynced && onSynced();
          } else {
            toast.error(`Sync failed: ${data.error || "Unknown"}`);
          }
          loadHistory();
        }
      } catch (e) { /* silent */ }
    }, 2500);
    return () => clearInterval(t);
  }, [job, onSynced, loadHistory]);

  const startSync = async () => {
    if (!config?.has_api_key) {
      toast.error("Set the API key in Config first");
      onConfigure && onConfigure();
      return;
    }
    if (start < FLOOR_DATE) {
      toast.warning(`Start date clamped to ${FLOOR_DATE}`);
    }
    setStarting(true);
    try {
      const { data } = await api.post("/worklogs/sync", {
        start, end, force_refetch: forceRefetch,
      });
      setJob({ ...data, pages: 0, rows_fetched: 0, inserted: 0, updated: 0 });
      toast.success("Sync started");
      loadHistory();
    } catch (e) { toast.error(errMsg(e, "Sync failed to start")); }
    finally { setStarting(false); }
  };

  const running = job?.status === "running";

  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center gap-2 flex-wrap">
          <Cloud className="h-4 w-4 text-sky-600" />
          <div className="font-semibold text-sm">Sync from Iceberg CMS</div>
          {config?.has_api_key
            ? <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]" variant="outline">API key saved</Badge>
            : <Badge className="bg-amber-50 text-amber-700 border-amber-200 text-[10px]" variant="outline">not configured</Badge>}
          <Button variant="ghost" size="sm" onClick={onConfigure}
            className="ml-auto h-8 text-xs" data-testid="worklogs-configure-btn">
            <Settings2 className="h-3.5 w-3.5 mr-1" />Configure
          </Button>
        </div>
        <div className="text-[11px] text-zinc-500 -mt-1">
          Pulls 500 rows per page. First pull is floored at <b>{FLOOR_DATE}</b>.
          {config?.last_synced_at && (
            <> Last synced: <b>{fmtDateTime(config.last_synced_at)}</b>
            {config.last_synced_range && <> ({config.last_synced_range.start} → {config.last_synced_range.end})</>}.</>
          )}
        </div>

        <div className="grid md:grid-cols-4 gap-2 items-end">
          <div>
            <Label className="text-[10px] uppercase tracking-wide text-zinc-500">Start</Label>
            <Input type="date" min={FLOOR_DATE} value={start}
              onChange={(e) => setStart(e.target.value)}
              disabled={running}
              data-testid="worklogs-sync-start" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wide text-zinc-500">End</Label>
            <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)}
              disabled={running}
              data-testid="worklogs-sync-end" />
          </div>
          <label className="flex items-center gap-2 text-xs h-9 text-zinc-600">
            <input type="checkbox" checked={forceRefetch}
              onChange={(e) => setForceRefetch(e.target.checked)}
              disabled={running}
              data-testid="worklogs-force-refetch" />
            Force overwrite existing
          </label>
          <Button onClick={startSync} disabled={running || starting}
            data-testid="worklogs-sync-start-btn">
            {(running || starting)
              ? <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              : <Download className="h-4 w-4 mr-1" />}
            {running ? "Syncing…" : "Sync now"}
          </Button>
        </div>

        {job && (
          <div className={`rounded border p-2.5 text-xs space-y-1
            ${job.status === "running" ? "bg-sky-50 border-sky-200"
              : job.status === "done" ? "bg-emerald-50 border-emerald-200"
              : "bg-red-50 border-red-200"}`}
            data-testid="worklogs-sync-progress">
            <div className="flex items-center gap-2">
              {job.status === "running" && <Loader2 className="h-3.5 w-3.5 animate-spin text-sky-600" />}
              {job.status === "done" && <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />}
              {job.status === "failed" && <BadgeAlert className="h-3.5 w-3.5 text-red-600" />}
              <span className="font-semibold capitalize">{job.status}</span>
              <span className="text-zinc-500">· {job.start} → {job.end}</span>
            </div>
            <div className="text-zinc-700">
              Pages: <b>{fmtInt(job.pages)}</b> ·
              Rows fetched: <b>{fmtInt(job.rows_fetched)}</b> ·
              New: <b>{fmtInt(job.inserted)}</b> ·
              Updated: <b>{fmtInt(job.updated)}</b>
            </div>
            {job.error && <div className="text-red-700 break-words">{job.error}</div>}
          </div>
        )}

        {history.length > 0 && (
          <details className="text-xs">
            <summary className="cursor-pointer text-zinc-500 hover:text-zinc-800">
              Recent syncs ({history.length})
            </summary>
            <div className="mt-2 space-y-1">
              {history.slice(0, 10).map((h) => (
                <div key={h.job_id}
                  className="flex items-center gap-2 rounded border border-zinc-200 px-2 py-1.5 bg-white">
                  <Badge variant="outline" className={`text-[10px]
                    ${h.status === "done" ? "border-emerald-200 text-emerald-700 bg-emerald-50"
                      : h.status === "failed" ? "border-red-200 text-red-700 bg-red-50"
                      : "border-sky-200 text-sky-700 bg-sky-50"}`}>{h.status}</Badge>
                  <span className="text-zinc-700">{h.start} → {h.end}</span>
                  <span className="text-zinc-500 ml-auto">
                    {fmtInt(h.rows_fetched)} rows · {fmtDateTime(h.created_at)}
                  </span>
                </div>
              ))}
            </div>
          </details>
        )}
      </CardContent>
    </Card>
  );
}

/* ── Main page ─────────────────────────────────────────────────── */
export default function WorkLogsPage() {
  const [config, setConfig] = useState(null);
  const [configOpen, setConfigOpen] = useState(false);
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(500);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [employeeFilter, setEmployeeFilter] = useState("");
  const [clientFilter, setClientFilter] = useState("");
  const [startFilter, setStartFilter] = useState("");
  const [endFilter, setEndFilter] = useState("");

  const loadConfig = useCallback(async () => {
    try {
      const { data } = await api.get("/worklogs/config");
      setConfig(data);
    } catch (e) { /* silent */ }
  }, []);

  const loadSummary = useCallback(async () => {
    try {
      const { data } = await api.get("/worklogs/summary");
      setSummary(data);
    } catch (e) { /* silent */ }
  }, []);

  const loadRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, per_page: perPage };
      if (search) params.search = search;
      if (employeeFilter) params.employee = employeeFilter;
      if (clientFilter) params.client_name = clientFilter;
      if (startFilter) params.start = startFilter;
      if (endFilter) params.end = endFilter;
      const { data } = await api.get("/worklogs", { params });
      setRows(data.rows || []);
      setTotal(data.total || 0);
      setTotalPages(data.total_pages || 1);
    } catch (e) { toast.error(errMsg(e, "Failed to load work logs")); }
    finally { setLoading(false); }
  }, [page, perPage, search, employeeFilter, clientFilter, startFilter, endFilter]);

  useEffect(() => { loadConfig(); loadSummary(); }, [loadConfig, loadSummary]);
  useEffect(() => { loadRows(); }, [loadRows]);

  const onSynced = useCallback(() => {
    loadSummary();
    loadRows();
    loadConfig();
  }, [loadSummary, loadRows, loadConfig]);

  const applyFilter = () => { setPage(1); loadRows(); };
  const resetFilter = () => {
    setSearch(""); setEmployeeFilter(""); setClientFilter("");
    setStartFilter(""); setEndFilter(""); setPage(1);
  };

  const columns = useMemo(() => [
    { key: "entry_date", label: "Date", w: "w-[100px]" },
    { key: "employee", label: "Employee", w: "w-[160px]" },
    { key: "client_name", label: "Client", w: "w-[180px]" },
    { key: "job_ref", label: "Job / Ref", w: "w-[120px]" },
    { key: "description", label: "Description", w: "" },
    { key: "hours", label: "Hours", w: "w-[80px] text-right" },
    { key: "cost", label: "Cost", w: "w-[100px] text-right" },
    { key: "status", label: "Status", w: "w-[100px]" },
  ], []);

  return (
    <div className="p-6 space-y-4 max-w-[1400px]" data-testid="worklogs-page">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ClipboardList className="h-6 w-6 text-sky-600" />Work Logs
          </h1>
          <div className="text-sm text-zinc-500 mt-1">
            Time and work-element records synced from Iceberg CMS.
            {summary && (
              <>
                {" "}<b>{fmtInt(summary.total_rows)}</b> row(s)
                {summary.earliest_entry && summary.latest_entry
                  && <> · {summary.earliest_entry} → {summary.latest_entry}</>}
              </>
            )}
          </div>
        </div>
      </div>

      <SyncPanel config={config} onConfigure={() => setConfigOpen(true)} onSynced={onSynced} />

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="grid md:grid-cols-6 gap-2 items-end">
            <div className="md:col-span-2">
              <Label className="text-[10px] uppercase tracking-wide text-zinc-500">Search</Label>
              <div className="relative">
                <Search className="h-3.5 w-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400" />
                <Input value={search} onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && applyFilter()}
                  placeholder="Description, employee, client, ref…"
                  className="pl-7" data-testid="worklogs-search-input" />
              </div>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wide text-zinc-500">Employee</Label>
              <Input value={employeeFilter} onChange={(e) => setEmployeeFilter(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && applyFilter()}
                data-testid="worklogs-employee-filter" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wide text-zinc-500">Client</Label>
              <Input value={clientFilter} onChange={(e) => setClientFilter(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && applyFilter()}
                data-testid="worklogs-client-filter" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wide text-zinc-500">From</Label>
              <Input type="date" value={startFilter} onChange={(e) => setStartFilter(e.target.value)}
                data-testid="worklogs-start-filter" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wide text-zinc-500">To</Label>
              <Input type="date" value={endFilter} onChange={(e) => setEndFilter(e.target.value)}
                data-testid="worklogs-end-filter" />
            </div>
          </div>
          <div className="flex items-center gap-2 mt-3 flex-wrap">
            <Button size="sm" onClick={applyFilter} data-testid="worklogs-apply-filter">
              <Search className="h-3.5 w-3.5 mr-1" />Apply
            </Button>
            <Button variant="ghost" size="sm" onClick={resetFilter}
              data-testid="worklogs-reset-filter">Reset</Button>
            <div className="ml-auto flex items-center gap-2">
              <Label className="text-xs text-zinc-500">Per page</Label>
              <Select value={String(perPage)} onValueChange={(v) => { setPerPage(Number(v)); setPage(1); }}>
                <SelectTrigger className="h-8 text-xs w-24" data-testid="worklogs-per-page">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                  <SelectItem value="250">250</SelectItem>
                  <SelectItem value="500">500</SelectItem>
                  <SelectItem value="1000">1000</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="ghost" size="sm" onClick={loadRows} disabled={loading}
                data-testid="worklogs-reload">
                <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading && rows.length === 0 ? (
            <div className="p-8 text-sm text-zinc-500 text-center">
              <Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading…
            </div>
          ) : rows.length === 0 ? (
            <div className="p-8 text-sm text-zinc-500 text-center" data-testid="worklogs-empty">
              <ClipboardList className="h-8 w-8 mx-auto mb-2 opacity-40" />
              {total === 0 && !search && !employeeFilter && !clientFilter && !startFilter && !endFilter
                ? <>No work logs synced yet. Set the API key then click <b>Sync now</b>.</>
                : <>No work logs match your filter.</>}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="worklogs-table">
                <thead className="border-b bg-zinc-50 sticky top-0">
                  <tr className="text-left text-[10px] uppercase tracking-wide text-zinc-500">
                    {columns.map((c) => (
                      <th key={c.key} className={`py-2 px-3 ${c.w}`}>{c.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={r.external_id || i} className="border-b last:border-b-0 hover:bg-zinc-50/50"
                      data-testid={`worklogs-row-${r.external_id || i}`}>
                      <td className="py-1.5 px-3 whitespace-nowrap">{r.entry_date || "—"}</td>
                      <td className="py-1.5 px-3 truncate max-w-[160px]">{r.employee || "—"}</td>
                      <td className="py-1.5 px-3 truncate max-w-[180px]">{r.client_name || "—"}</td>
                      <td className="py-1.5 px-3 font-mono text-xs">{r.job_ref || "—"}</td>
                      <td className="py-1.5 px-3">
                        <div className="line-clamp-2 text-zinc-700">{r.description || "—"}</div>
                      </td>
                      <td className="py-1.5 px-3 text-right tabular-nums">
                        {r.hours != null ? r.hours.toFixed(2) : "—"}
                      </td>
                      <td className="py-1.5 px-3 text-right tabular-nums">
                        {r.cost != null ? `$${r.cost.toFixed(2)}` : "—"}
                      </td>
                      <td className="py-1.5 px-3 text-xs">{r.status || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {total > 0 && (
            <div className="flex items-center justify-between p-3 border-t bg-zinc-50/60 text-xs"
              data-testid="worklogs-pagination">
              <div className="text-zinc-500">
                Showing <b>{fmtInt((page - 1) * perPage + 1)}</b>–
                <b>{fmtInt(Math.min(page * perPage, total))}</b> of <b>{fmtInt(total)}</b>
              </div>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="sm" className="h-7 text-xs"
                  disabled={page <= 1} onClick={() => setPage(1)}
                  data-testid="worklogs-page-first">First</Button>
                <Button variant="outline" size="sm" className="h-7 w-7 p-0"
                  disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}
                  data-testid="worklogs-page-prev">
                  <ChevronLeft className="h-3.5 w-3.5" />
                </Button>
                <span className="px-2 tabular-nums">Page <b>{page}</b> / {totalPages}</span>
                <Button variant="outline" size="sm" className="h-7 w-7 p-0"
                  disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  data-testid="worklogs-page-next">
                  <ChevronRight className="h-3.5 w-3.5" />
                </Button>
                <Button variant="outline" size="sm" className="h-7 text-xs"
                  disabled={page >= totalPages} onClick={() => setPage(totalPages)}
                  data-testid="worklogs-page-last">Last</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <ConfigDialog open={configOpen} onOpenChange={setConfigOpen}
        config={config}
        onSaved={(d) => { setConfig(d); loadConfig(); }} />
    </div>
  );
}
